import React, {useEffect, useState} from "react";
import {useTranslation} from 'react-i18next';
import {connect, useSelector} from 'react-redux';
import {useParams, useNavigate} from "react-router-dom";
import {bindActionCreators} from "redux";
import {Container, Grid, Paper, Typography, Box, useTheme, useMediaQuery, Drawer} from '@mui/material';
import courseActions from '../../../store/course/actions';
import MainMenu from "../../core/mainMenu";
import {Course, Task as ITASK, Lesson} from "../../../common/course";
import Task from "../../core/task";
import {selectCourseById, selectLessonById, selectLessonsByCourseId, selectTasksByCourseId} from "../../../store/course/selectors";
import {CommonState} from "../../../store";
import {SIDE_BAR_WIDTH} from "../../../consts/userInterface";
import {calculateProgress} from "../../../utils";
import APP_URLs from "../../../consts/url";

export type Props = {
    fetchCourses: Function,
    fetchTasksByCourseId: Function,
}
const TaskSWithProgress = ({
    tasks,
    onSetSelectedTask,
    numberPrefix,
    selectedTask,
    lessonId,
}: {
    tasks: ITASK[],
    onSetSelectedTask:
    Function,
    numberPrefix: number,
    selectedTask: ITASK | null,
    lessonId: Lesson['id'],
}) => {
    const isCompletedTask = (task: ITASK): boolean => {
        return task.results.score === task.results.maxScore;
    }
    const successColor = '#18d85d';
    const defaultColor = '#ccc';
    const selectedColor = '#fff';
    const defineSelectedTaskColor = (task: ITASK, index: number): string => {
        if (!selectedTask && !index && task.lessonId === lessonId) {
            return selectedColor;
        }
        if ((task.lessonId === lessonId) && (task.id === selectedTask?.id)) {
            return selectedColor;
        }
        return isCompletedTask(task) ? successColor : defaultColor;
    };
    return (
        <Box
            component={'ul'}
            sx={{
                padding: 0,
                listStyle: 'none',
            }}
        >
            {tasks.map((task, index) => (
                <Box
                    component={'li'}
                    key={index}
                    onClick={() => onSetSelectedTask(task)}
                    sx={{
                        mb: 4,
                        paddingLeft: 2,
                        color: defineSelectedTaskColor(task, index),
                        position: 'relative',
                        cursor: 'pointer',
                        '&::before': {
                            content: '""',
                            display: 'block',
                            height: '4px',
                            width: '4px',
                            borderRadius: '4px',
                            position: 'absolute',
                            backgroundColor: isCompletedTask(task) ? successColor : defaultColor,
                            top: 0,
                            bottom: 0,
                            left: '3px',
                            margin: 'auto',
                        },
                        '&::after': index + 1 === tasks.length
                            ? {}
                            : {
                                content: '""',
                                position: 'absolute',
                                display: 'block',
                                width: '2px',
                                height: '150%',
                                top: '90%',
                                left: '4.5px',
                                backgroundColor: isCompletedTask(task) ? successColor : defaultColor,
                            }
                    }}
                >
                    <Typography
                        component={'span'}
                        sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                        }}
                    >
                        {`${numberPrefix}.${index + 1} ${task.title}`}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
const View = ({fetchCourses, fetchTasksByCourseId}: Props) => {
    const {lessonId, courseId} = useParams<{lessonId: string, courseId: string}>();
    const course: Course = useSelector((state: CommonState) => selectCourseById(state, String(courseId)));
    const tasks: ITASK[] = useSelector((state: CommonState) => selectTasksByCourseId(state, String(courseId)));
    const lesson: Lesson = useSelector((state: CommonState) => selectLessonById(state, String(lessonId)));
    const lessons: Lesson[] = useSelector((state: CommonState) => selectLessonsByCourseId(state, String(courseId)));
    const [selectedTask, setSelectedTask] = useState<ITASK | null>(null);
    const navigate = useNavigate();
    const onSetSelectedTask = (task: ITASK) => {
        window.scrollTo(0, 0);
        navigate(`${APP_URLs.LESSON}/${course.id}/${task.lessonId}`)
        setSelectedTask(task);
    }
    const theme = useTheme();
    const {t: translate, i18n: languageCtx} = useTranslation();
    //@ts-expect-error
    const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));
    useEffect(() => {
        fetchCourses(courseId)
        fetchTasksByCourseId(courseId)
    }, []);
    if (!tasks.length || !course) {
        return null;
    }
    return (
        <>
            <MainMenu />
            <Box sx={{display: 'flex'}}>
                {!isSmallScreen && (
                    <Drawer
                        variant={'permanent'}
                        anchor="left"
                        sx={{
                            width: `${SIDE_BAR_WIDTH}px`,
                            flexShrink: '0',
                            position: 'relative',
                            top: '50px',
                            '& .MuiDrawer-paper': {
                                width: `${SIDE_BAR_WIDTH}px`,
                                padding: 2,
                                paddingLeft: 4,
                                paddingTop: '60px',
                                boxSizing: 'border-box',
                                backgroundColor: 'secondary.light',
                                color: '#fff'
                            },
                        }}
                    > 
                        <Typography
                            sx={{mb: 1}}
                        >
                            {`${translate('translation:menuProgress')} ${calculateProgress(Math.max(course.results.maxScore, course.results.score), Math.min(course.results.maxScore, course.results.score))}%`}
                        </Typography>
                        {lessons.map((lessonInstance, index) => (
                            <Box key={`menu-${lessonInstance.id}`}>
                                <Typography>{`${index + 1} ${lessonInstance.title}`}</Typography>
                                <TaskSWithProgress
                                    selectedTask={selectedTask} tasks={tasks.filter((taskItem => taskItem.lessonId === lessonInstance.id))}
                                    onSetSelectedTask={onSetSelectedTask} numberPrefix={index + 1}
                                    lessonId={String(selectedTask?.lessonId || lessonId)}
                                />
                            </Box>
                        ))}
                    </Drawer>
                )}
                <Box component='main' sx={{flexGrow: 1, maxWidth: '100%'}}>
                    <Container sx={{py: 2}}>
                        <Grid container>
                            {tasks.length
                            ? (
                                <Task
                                    //@ts-expect-error
                                    courseId={courseId}
                                    //@ts-expect-error
                                    task={selectedTask || tasks.find(task => task.lessonId === lessonId)}
                                    //@ts-expect-error
                                    onNextTask={(taskId: ITASK["id"]) => {onSetSelectedTask(tasks.find(task => task.id === taskId))}}
                                />
                            )
                            : null
                            }
                        </Grid>
                    </Container>
                </Box>
            </Box>
        </>
    )
}

//@ts-expect-error
const dispatchProps = (dispatch) => ({
    fetchTasksByCourseId: bindActionCreators(courseActions.fetchTasksByCourseId, dispatch),
    fetchLesson: bindActionCreators(courseActions.fetchLesson, dispatch),
    fetchCourses: bindActionCreators(courseActions.fetchCourses, dispatch),
    fetchLessons: bindActionCreators(courseActions.fetchLessons, dispatch),
})

export default connect(null, dispatchProps)(View);